// ignore-string-externalization
//
import React from 'react';
import { useTracking } from '../Tracking';
import { MiniCarouselNarrativeModule } from '@spotify-internal/frodor-modules';
import { PageBreakerModule } from './PageBreakerModule';
import { RelatedLinksModule } from './RelatedLinksModule';
import { FAQLinksModule } from './FAQLinksModule';
import { MiniCarouselNoHeroModule } from './MiniCarouselNoHeroModule';
import { HeaderModule } from './HeroHeaderModule';
import { ModuleCarousel } from './ModuleCarousel/ModuleCarousel';
import { SingleTestimonialModule } from './SingleTestimonialModule';
import { TwoUpModule } from './TwoUpFeaturedContentModule';
import { ThreeUpModule } from './ThreeUpFeaturedContentModule';
import { heroNarrativeCarouselModuleCreator } from './HeroNarrativeCarouselModule';
import { HeroBodyModule } from './HeroBodyModule';
import { HeroTestimonial } from './HeroNarrativeCarouselModule/Options/HeroTestimonial';
import { HeroDescription } from './HeroNarrativeCarouselModule/Options/HeroDescription';
import { HeroCaseStudy } from './HeroNarrativeCarouselModule/Options/HeroCaseStudy';
import { GroupTestimonialModule } from './GroupTestimonialModule';
import { EventListModule } from './EventListModule';
import { EditorialGridModule } from './EditorialGridModule';
import { VideoModule } from './VideoModule';
import { RelatedStoriesListModule } from './RelatedStoriesListModule';
import { ArticleHeaderModule } from './ArticleHeaderModule';
import { ArticleModule } from './ArticleModule';
import { FeaturedContentModule } from './FeaturedContentModule';
import { AnimationContext, useAnimations, customThreshold } from '../ui';
import { ArticleFeedbackModule } from './ArticleFeedbackModule';
import { ModuleType } from './types';

export const Module = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  ModuleType
>((props, ref) => {
  const {
    contentType,
    shouldAnimate = true,
    isVisible = false,
    title = '',
  } = props;
  const ModuleComponent = MODULE_COMPONENTS[contentType];
  const shown = React.useRef(false);
  const { animationContext, animationRef } = useAnimations(
    contentType,
    shouldAnimate,
    customThreshold(contentType),
  );
  const { sendEvent } = useTracking();

  React.useEffect(() => {
    if (isVisible && ModuleComponent && !shown.current) {
      shown.current = true;
      sendEvent({
        eventCategory: `Frodor - module-${contentType}`,
        eventAction: 'view',
        eventLabel: title,
      });
    }
  }, [isVisible]);

  if (ModuleComponent) {
    return (
      <AnimationContext.Provider value={animationContext}>
        <ModuleComponent {...props} ref={mergeRefs(ref, animationRef)} />
      </AnimationContext.Provider>
    );
  }
  return <>Module type "{contentType}" not found</>;
});

const Carousel = React.forwardRef((props: any, ref: any) => (
  <ModuleCarousel {...props} ref={ref} Module={Module} />
));

const MODULE_COMPONENTS: { [key: string]: React.FC<any> | undefined } = {
  articleModule: ArticleModule,
  faqLinksModule: FAQLinksModule,
  pageBreakerModule: PageBreakerModule,
  editorialGridModule: EditorialGridModule,
  singleFeatureModule: MiniCarouselNoHeroModule,
  singleTestimonial: SingleTestimonialModule,
  groupTestimonial: GroupTestimonialModule,
  featuredContent2Up: TwoUpModule,
  featuredContent3up: ThreeUpModule,
  relatedLinksModule: RelatedLinksModule,
  headerModule: HeaderModule,
  heroBodyModule: HeroBodyModule,
  heroBodyCarousel: Carousel,
  heroHeaderCarousel: Carousel,
  heroNarrativeCarousel: heroNarrativeCarouselModuleCreator(Module),
  heroNarrativeQuoteSlide: HeroTestimonial,
  heroNarrativeTextSlide: HeroDescription,
  heroCaseStudyCover: HeroCaseStudy,
  infoHelpfulModule: ArticleFeedbackModule,
  eventList: EventListModule,
  relatedStoriesList: RelatedStoriesListModule,
  videoModule: VideoModule,
  featuredContentModule: FeaturedContentModule,
  articleHeaderModule: ArticleHeaderModule,
  miniCarouselNarrative: MiniCarouselNarrativeModule,
};

const mergeRefs = (
  ...refs: Array<React.MutableRefObject<any> | Function | null>
) => {
  const filteredRefs: Array<React.MutableRefObject<any> | Function | null> =
    refs.filter(Boolean);
  if (!filteredRefs.length) return null;
  if (filteredRefs.length === 1) return filteredRefs[0];
  return (inst: any) => {
    for (const elementRef of filteredRefs) {
      if (typeof elementRef === 'function') {
        elementRef(inst);
      } else if (elementRef) {
        elementRef.current = inst;
      }
    }
  };
};

export { ModuleCarousel };
